import { autoinject } from "aurelia-dependency-injection";
import { I18N } from "aurelia-i18n";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { MobileHelper } from "../../../../../../../../helpers/mobile-helper";
import { CompanyModel } from "../../../../../../../../models/companies/company.models";
import { ItemSource, DataGridColumn, DataType, QueryFilter, SourceType } from "../../../../../../../../models/queries/queries.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { CompanyService } from "../../../../../../../../services/modules/company-client.service";
import { ClipboardCopyService } from "../../../../../../../../utilities/clipboard-copy-service";
import { DataGrid } from "../../../../../../../components/data-grid/data-grid";
import { RightshipDataGridColumn } from "../../../../../../../components/rightship-data-grid/rightship-data-grid";
import { RightshipConfirmationDialogModel } from "../../../../../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { ChildComponent } from "../../../../../child-component";
import { useView } from "aurelia-framework";

@autoinject
@useView('views/pages/modules/company/tabs/overview/tabs/vetting-contacts/company-vetting-contacts.html')
export class CompanyVettingContacts extends ChildComponent<CompanyModel>
{
    public itemSource: ItemSource;

    public columns: DataGridColumn[] = [];

    public dataGridVM: DataGrid;

    constructor(notificationsService: NotificationClientService, i18N: I18N, private dialogClient: DialogClientService,
        private companyService: CompanyService, private clipboardCopyService : ClipboardCopyService) {
        super(notificationsService, i18N);
    }

    onActivated() {
        this.columns = this.getColumns();
        this.itemSource = this.getItemSource();
    }

    private getColumns() {
        const columns: RightshipDataGridColumn[] =
            [{
                dataType: DataType.CustomTemplate,
                value: '<div class="vetting-contact"><p class="value">${item.name}</p></div>',
                name: "name",
                title: "Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: 1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 2,
                dataType: DataType.CustomTemplate,
                name: 'email',
                title: 'Email',
                instancePerRow: true,
                value: '<div class="vetting-contact row nowrap"><p class="value">${item.email}</p><comp-icon class="icon-copy" icon="copy" click.trigger="copy.click(`${item.email}`)"</comp-icon></div>',
                isSortable: false,
                parameter: {
                    item : null,
                    service : this.clipboardCopyService,
                    copy: {
                        click: (item) => {
                            this.copy(item)
                        }
                    }
                }
            }, {
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                name: 'phoneNumber',
                title: 'Phone',
                isSortable: false,
            }, {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                name: 'mobileNumber',
                title: 'Mobile Number',
                isSortable: false,
            },{
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.Default,
                name: 'role',
                title: 'Role',
                isSortable: false,
            }, {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.Boolean,
                name: 'isPrimaryContact',
                title: 'Primary Contact',
                isSortable: false,
            },{
                columnOrder: 7,
                sortOrder: 7,
                dataType: DataType.Default,
                name: 'comment',
                title: 'Comment',
                isSortable: false,
                ellipsis: 100,
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "200px",
            },
            {
                columnOrder: 10,
                sortOrder: 10,
                dataType: DataType.CustomTemplate,
                name: "menu",
                instancePerRow: true,
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems" if.bind="menuItems.length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: [{
                        isVisible: true,
                        click: ($event, item) => {
                            $event.stopPropagation();
                            this.editVettingContact(item);
                        },
                        item: { text: "Edit" },
                        classList: ''
                    },
                    {
                        isVisible: true,
                        click: ($event, item) => {
                            $event.stopPropagation();
                            this.showDeleteDialog(item);
                        },
                        item: { text: "Remove" },
                        classList: ''
                    }
                    ]
                }
            }];

        return columns;
    }

    public addVettingContact() {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.EditVettingContactDialog, {
            onConfirm: () => {
                this.dataGridVM.reloadData(null, null);
            },
            id: null,
            companyId: this.item.companyId
        }, true, false, false, 'right');
    }

    public editVettingContact(item: any) {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.EditVettingContactDialog, {
            onConfirm: () => {
                this.dataGridVM.reloadData(null, null);
            },
            id: item ? item.id : null
        }, true, false, false, 'right');
    }

    showDeleteDialog(item) {
        const model = new RightshipConfirmationDialogModel();
        model.confirm = this.i18N.tr('submit-text');
        model.messages.push(`<b>Do you want remove ${item.name} from ${this.item.fullName}?</b>`);
        model.messages.push(`Their contact details will be removed from vetting contacts.`);
        model.title = `${this.i18N.tr('remove-text')} ${item.name}`;
        model.cancel = "cancel"
        model.onConfirm = () => this.deleteVettingContact(item);
        model.confirmClass = "danger";
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false, 'right');
    }

    private deleteVettingContact(item)
    {
        this.companyService.removeCompanyVettingContact(item.companyVettingContactId).then(r => {
            if(r){
                this.notificationService.show("Vetting contact remove successfully", "", NotificationType.Success);
                this.dataGridVM.reloadData(null, null);
            }
            else
                this.notificationService.show("Vetting contact could not be removed", "", NotificationType.Error);
        })
        .catch(e => {
            this.notificationService.show("Error removing vetting contact", e.message, NotificationType.Error);
        })
    }

    private getItemSource() {
        const filters = [QueryFilter.getQuery("companyId", this.item.companyId, "eq", DataType.Number, false)]
        return {
            sourceType: SourceType.ODataUrl,
            source: "odata/CompanyVettingContacts",
            columns: this.columns,
            initialFilters: filters
        };
    }

    copy(item) {
        this.clipboardCopyService.copy(item)
    }
}